import { createFileRoute, Link } from "@tanstack/react-router";
import { Button } from "@/components/ui/button";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import {
  COIN_PRICE_CENTS,
  MIN_COINS,
  formatEuro,
  grossFromNet,
} from "@/lib/salonturbo";
import {
  Scissors,
  Search,
  MousePointerClick,
  Phone,
  CalendarCheck2,
  UserCircle,
  Sparkles,
  ArrowRight,
  Clock,
  Check,
  MapPin,
} from "lucide-react";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import salonInterior from "@/assets/salon-interior.jpg";
import hairBob from "@/assets/hair-bob.jpg";

const FAQ = [
  {
    q: "Wie schnell ist der erste Boost möglich?",
    a: "Nach der Registrierung prüft Google die neue Kampagne einmalig – das dauert in der Regel bis zu 24 Stunden. Deshalb ist der früheste Boost-Tag erst der nächste Tag. Danach kannst du auch den heutigen Tag auswählen.",
  },
  {
    q: "Was sieht der Kunde auf der Salon-Landingpage?",
    a: "Deine Salon-Landingpage zeigt deinen Salon-Namen, Bilder, Adresse, Öffnungszeiten und einen großen Anruf-Button. An geboosteten Tagen erscheint zusätzlich der Hinweis, dass noch Termine frei sind.",
  },
  {
    q: "Muss ich als Salon selbst Anzeigen schalten?",
    a: "Nein. Die Google-Ads-Kampagne läuft über unser zentrales Konto. Du wählst nur die Tage im Kalender aus – wir übernehmen Anzeigenerstellung, Schaltung, Budget und Stopp-Zeit.",
  },
  {
    q: "Was passiert an Tagen ohne Boost?",
    a: "An Nicht-Boost-Tagen läuft keine Werbung und es entstehen keine Kosten. Deine Salon-Landingpage bleibt online, nur ohne den Hinweis auf kurzfristig freie Termine.",
  },
  {
    q: "Wie viel kostet ein Boost-Tag?",
    a: `Ein Coin kostet ${formatEuro(COIN_PRICE_CENTS)} netto (${formatEuro(grossFromNet(COIN_PRICE_CENTS))} inkl. 19 % MwSt.) und entspricht einem geboosteten Tag. Der Einstieg ist ab ${MIN_COINS} Coins möglich.`,
  },
];

export const Route = createFileRoute("/ablauf")({
  head: () => ({
    meta: [
      { title: "So funktioniert SalonTurbo – Ablauf vom Boost bis zum Termin" },
      {
        name: "description",
        content:
          "Wie SalonTurbo im Alltag funktioniert: Salon wählt freie Tage, Kunde findet die Anzeige, ruft an und bucht den Termin. Schritt für Schritt erklärt.",
      },
      {
        property: "og:title",
        content: "So funktioniert SalonTurbo – Ablauf vom Boost bis zum Termin",
      },
      {
        property: "og:description",
        content:
          "Vom leeren Stuhl zum Termin: Salon wählt Tage, SalonTurbo schaltet Anzeigen, Kunden rufen direkt an.",
      },
      { property: "og:type", content: "article" },
      { property: "og:url", content: "https://salonturbo.de/ablauf" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: "https://salonturbo.de/ablauf" }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: FAQ.map((item) => ({
            "@type": "Question",
            name: item.q,
            acceptedAnswer: { "@type": "Answer", text: item.a },
          })),
        }),
      },
    ],
  }),
  component: Ablauf,
});

function Ablauf() {
  return (
    <div className="min-h-screen bg-background">
      <SiteHeader />
      <main>
        {/* 1 – Hero */}
        <section className="mx-auto max-w-5xl px-4 pb-12 pt-12 sm:pt-16">
          <p className="inline-flex items-center gap-2 rounded-full bg-primary/10 px-3 py-1 eyebrow">
            <Scissors className="size-3.5" /> Der klassische Ablauf
          </p>
          <h1 className="mt-5 max-w-3xl font-display text-4xl font-light leading-[1.1] sm:text-5xl">
            So funktioniert SalonTurbo: Vom leeren Stuhl zum Termin
          </h1>
          <p className="mt-5 max-w-2xl text-base leading-relaxed text-muted-foreground sm:text-lg">
            Du merkst eine Lücke im Kalender, wählst den Tag mit einem Klick – und genau dann werden
            lokale Kunden aufmerksam, die jetzt sofort einen Termin suchen. Keine Agentur, kein
            dauerhaftes Budget, nur der Ablauf, der wirklich zählt.
          </p>
          <div className="mt-7 flex flex-col gap-3 sm:flex-row">
            <Button asChild size="lg">
              <Link to="/auth">
                Salon einrichten <ArrowRight className="ml-1 size-4" />
              </Link>
            </Button>
            <Button asChild size="lg" variant="outline">
              <Link to="/preise">Preise ansehen</Link>
            </Button>
          </div>
        </section>

        {/* 2 – Visual anchor */}
        <section className="mx-auto max-w-5xl px-4 pb-14">
          <div className="overflow-hidden rounded-3xl shadow-soft-lg">
            <img
              src={salonInterior}
              alt="Moderner Friseursalon mit freien Stühlen am Fenster"
              width={1280}
              height={960}
              loading="lazy"
              className="h-56 w-full object-cover sm:h-80"
            />
          </div>
        </section>

        {/* 3 – Parallel flow */}
        <section className="border-y bg-secondary/50">
          <div className="mx-auto max-w-5xl px-4 py-16">
            <h2 className="font-display text-2xl font-normal sm:text-4xl">
              Zwei Seiten, ein Ziel: volle Stühle
            </h2>
            <p className="mt-3 max-w-2xl text-muted-foreground">
              Auf der einen Seite stehst du als Salon-Inhaber. Auf der anderen Seite dein Kunde. SalonTurbo
              bringt euch genau dann zusammen, wenn es passt.
            </p>

            <div className="mt-10 grid gap-8 lg:grid-cols-2">
              {/* Salon side */}
              <div className="rounded-3xl border bg-card p-6 sm:p-8">
                <p className="eyebrow text-primary">Salon-Seite</p>
                <h3 className="mt-2 font-display text-xl font-medium sm:text-2xl">
                  Du entscheidest, wann Werbung läuft
                </h3>
                <ol className="mt-6 space-y-5">
                  {[
                    {
                      icon: UserCircle,
                      title: "Profil anlegen",
                      text: "Name, Adresse, Telefonnummer, Öffnungszeiten und Bilder eingeben – fertig ist deine Salon-Landingpage.",
                    },
                    {
                      icon: CalendarCheck2,
                      title: "Freie Tage wählen",
                      text: "Im Kalender markierst du die Tage, an denen noch Stühle frei sind – bis zu 30 Tage im Voraus.",
                    },
                    {
                      icon: Sparkles,
                      title: "Boost aktivieren",
                      text: "Pro Tag wird ein Coin eingelöst. Die Anzeigen starten automatisch und stoppen vor Ladenschluss.",
                    },
                  ].map((step, index) => (
                    <li key={step.title} className="flex gap-4">
                      <span className="flex size-8 shrink-0 items-center justify-center rounded-full bg-primary/10 font-display text-sm font-medium text-primary">
                        {index + 1}
                      </span>
                      <div>
                        <div className="flex items-center gap-2">
                          <step.icon className="size-4 text-primary" />
                          <span className="font-display font-medium">{step.title}</span>
                        </div>
                        <p className="mt-1 text-sm leading-relaxed text-muted-foreground">
                          {step.text}
                        </p>
                      </div>
                    </li>
                  ))}
                </ol>
              </div>

              {/* Customer side */}
              <div className="rounded-3xl border bg-card p-6 sm:p-8">
                <p className="eyebrow text-success">Kunden-Seite</p>
                <h3 className="mt-2 font-display text-xl font-medium sm:text-2xl">
                  Dein Kunde findet dich spontan
                </h3>
                <ol className="mt-6 space-y-5">
                  {[
                  {
                    icon: Search,
                    title: "Auf Google suchen",
                    text: 'Der Kunde tippt "Friseur heute" oder "Friseurtermin heute" und deinen Ort in die Suche.',
                  },
                  {
                    icon: MousePointerClick,
                    title: "Auf die Anzeige klicken",
                    text: "Er sieht deine lokale Anzeige und landet auf deiner Salon-Landingpage mit allen Infos.",
                  },
                  {
                    icon: Phone,
                    title: "Direkt anrufen",
                    text: "Ein großer Anruf-Button führt sofort zur Telefon-App – Terminvereinbarung im Gespräch.",
                  },
                  ].map((step, index) => (
                    <li key={step.title} className="flex gap-4">
                      <span className="flex size-8 shrink-0 items-center justify-center rounded-full bg-success/10 font-display text-sm font-medium text-success">
                        {index + 1}
                      </span>
                      <div>
                        <div className="flex items-center gap-2">
                          <step.icon className="size-4 text-success" />
                          <span className="font-display font-medium">{step.title}</span>
                        </div>
                        <p className="mt-1 text-sm leading-relaxed text-muted-foreground">
                          {step.text}
                        </p>
                      </div>
                    </li>
                  ))}
                </ol>
              </div>
            </div>

            {/* Connection hint */}
            <div className="mt-8 flex items-center justify-center gap-3 rounded-2xl border bg-background p-4 text-sm text-muted-foreground">
              <MapPin className="size-4 text-primary" />
              <span>
                Die Anzeigen werden nur in deiner Umgebung ausgespielt – auf Begriffe, die Menschen
                gerade jetzt suchen.
              </span>
            </div>
          </div>
        </section>

        {/* 4 – Typical day scenario */}
        <section className="mx-auto max-w-5xl px-4 py-16">
          <h2 className="font-display text-2xl font-normal sm:text-4xl">Ein typischer Tag mit SalonTurbo</h2>
          <div className="mt-8 grid gap-6 lg:grid-cols-[1fr_1.2fr]">
            <div className="overflow-hidden rounded-3xl shadow-soft">
              <img
                src={hairBob}
                alt="Frisch geschnittener glänzender Bob"
                width={1024}
                height={1280}
                loading="lazy"
                className="h-64 w-full object-cover sm:h-80 lg:h-full"
              />
            </div>
            <div className="rounded-3xl border bg-card p-6 sm:p-8">
              <ol className="relative space-y-8 before:absolute before:left-3.5 before:top-2 before:h-[calc(100%-1rem)] before:w-px before:bg-border">
                {[
                  { time: "8:00 Uhr", text: "Der Salon öffnet. Zwei Absagen sind reingekommen – plötzlich sind Stühle frei." },
                  { time: "8:15 Uhr", text: "Du öffnest SalonTurbo und boostest den heutigen Tag mit einem Klick." },
                  { time: "9:00 Uhr", text: "Die ersten Anrufe kommen über die Salon-Landingpage." },
                  { time: "14:00 Uhr", text: "Die freien Stühle sind wieder belegt." },
                  { time: "18:00 Uhr", text: "Die Anzeige stoppt automatisch zwei Stunden vor Ladenschluss." },
                ].map((item) => (
                  <li key={item.time} className="relative pl-12">
                    <span className="absolute left-0 top-0.5 flex size-7 items-center justify-center rounded-full bg-primary/10 text-xs font-medium text-primary">
                      <Clock className="size-3.5" />
                    </span>
                    <p className="font-display font-medium">{item.time}</p>
                    <p className="mt-1 text-sm leading-relaxed text-muted-foreground">{item.text}</p>
                  </li>
                ))}
              </ol>
            </div>
          </div>
        </section>

        {/* 5 – Technical background */}
        <section className="border-y bg-secondary/50">
          <div className="mx-auto max-w-4xl px-4 py-16">
            <h2 className="font-display text-2xl font-normal sm:text-4xl">Was im Hintergrund passiert</h2>
            <div className="mt-8 grid gap-5 sm:grid-cols-2">
              {[
                {
                  title: "Zentrale Kampagnenführung",
                  text: "Deine Anzeigen laufen über das SalonTurbo-Google-Ads-Konto. Du brauchst keinen eigenen Account und keine technische Erfahrung.",
                },
                {
                  title: "Automatischer Start und Stopp",
                  text: "Anzeigen starten um Mitternacht am Boost-Tag und enden zwei Stunden vor Ende deiner Öffnungszeiten.",
                },
                {
                  title: "Lokale Ausspielung",
                  text: 'Werbung wird nur in deiner Region geschaltet, auf Suchbegriffe wie "Friseur heute" oder "Friseurtermin heute".',
                },
                {
                  title: "Landingpage bleibt online",
                  text: "Auch an Nicht-Boost-Tagen ist deine Salonseite erreichbar – nur ohne den Hinweis auf kurzfristig freie Termine.",
                },
              ].map((item) => (
                <div key={item.title} className="rounded-2xl border bg-card p-6">
                  <h3 className="font-display text-lg font-medium">{item.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{item.text}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* 6 – FAQ */}
        <section className="mx-auto max-w-3xl px-4 py-16">
          <h2 className="font-display text-2xl font-normal sm:text-4xl">Häufige Fragen zum Ablauf</h2>
          <Accordion type="single" collapsible className="mt-6">
            {FAQ.map((item, index) => (
              <AccordionItem key={item.q} value={`faq-${index}`}>
                <AccordionTrigger className="text-left">{item.q}</AccordionTrigger>
                <AccordionContent className="text-muted-foreground">{item.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </section>

        {/* 7 – Final CTA */}
        <section className="border-t bg-secondary/50">
          <div className="mx-auto max-w-4xl px-4 py-16">
            <div className="rounded-3xl bg-card p-6 text-center sm:p-8">
              <h2 className="font-display text-2xl font-normal sm:text-3xl">
                Bereit, deine ersten freien Stühle zu füllen?
              </h2>
              <p className="mx-auto mt-3 max-w-lg text-sm text-muted-foreground">
                In wenigen Minuten eingerichtet – danach reicht ein Klick pro Tag, um kurzfristige
                Lücken sichtbar zu machen.
              </p>
              <div className="mt-6 flex flex-col justify-center gap-3 sm:flex-row">
                <Button asChild size="lg">
                  <Link to="/auth">
                    SalonTurbo einrichten <ArrowRight className="ml-1 size-4" />
                  </Link>
                </Button>
                <Button asChild size="lg" variant="outline">
                  <Link to="/preise">Preise ansehen</Link>
                </Button>
              </div>
              <ul className="mt-6 flex flex-wrap justify-center gap-x-6 gap-y-2 text-sm text-muted-foreground">
                {[
                  "Keine Agenturkosten",
                  "Keine Mindestlaufzeit",
                  "Jederzeit pausierbar",
                ].map((item) => (
                  <li key={item} className="flex items-center gap-2">
                    <Check className="size-4 shrink-0 text-success" /> {item}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>
      </main>

      <SiteFooter />
    </div>
  );
}
